import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { InitialDataResponseInterface } from '../services/exports/Interfaces';
import InitialLoadingManager from '../services/api/InitialLoadingManager';
import { Response } from '../services/exports/Types';
import { InitialDataStoreInterface, StoreInterface } from '../store/types';
import useAuth from '../hooks/global/useAuth';
import { useIsMount } from '../hooks/utility/useIsMount';
import { REDUX_STORE_SLICES } from '../services/exports/Constants';
import useMobileApp from '../hooks/mobile-app/useMobileApp';

interface Props {
  children: any;
}

export interface InitialDataContextInterface {
  data: InitialDataStoreInterface;
  loading: boolean;
  refresh: (
    slug?: null | string,
    hideLoading?: boolean,
  ) => Promise<Response<InitialDataResponseInterface | InitialDataStoreInterface>>;
  /**
   * `retryWithDefault`: without a slug, the one saved from the last visit is tried; it may have been
   * renamed since, so the route's own load (the root, a table's link) falls back to the franchise's
   * default restaurant on a 404. An in-session reload of the current restaurant doesn't.
   */
  forceRefresh: (
    slug?: null | string,
    hideLoading?: boolean,
    retryWithDefault?: boolean,
  ) => Promise<Response<InitialDataResponseInterface | InitialDataStoreInterface>>;
}

export const InitialDataContext = React.createContext<InitialDataContextInterface>(null);

export default function InitialDataProvider(props: Props) {
  const { children } = props;

  const data = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA]);

  const isMount = useIsMount();
  const { isLoggedIn } = useAuth();

  const [loading, setLoading] = useState(false);
  const toggleLoading = () => setLoading((prev) => !prev);
  const { isMobileApp } = useMobileApp();

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (isMobileApp && isLoggedIn) {
      forceRefresh();

      return;
    }

    !isMount && isLoggedIn && forceRefresh();
  }, [isLoggedIn, isMobileApp]);

  const refresh = async (
    slug: string = null,
    hideLoading: boolean = false,
  ): Promise<Response<InitialDataResponseInterface | InitialDataStoreInterface>> => {
    if (loadingRef.current) {
      return {
        success: true,
        // @ts-expect-error Response is already being loaded
        response: {
          data,
        },
        status: 200,
      };
    }

    return forceRefresh(slug, hideLoading);
  };

  async function forceRefresh(
    slug: string = null,
    hideLoading: boolean = false,
    retryWithDefault: boolean = false,
  ): Promise<Response<InitialDataResponseInterface | InitialDataStoreInterface>> {
    toggleLoading();
    const response = await InitialLoadingManager.init(
      slug ?? dataRef.current?.company?.slug,
      hideLoading,
      retryWithDefault && !slug,
    );
    setTimeout(() => toggleLoading(), 300);

    return response;
  }

  const context = useMemo(
    () => ({
      data: dataRef.current,
      loading: loadingRef.current,
      refresh,
      forceRefresh,
    }),
    [dataRef.current, loadingRef.current],
  );

  return <InitialDataContext.Provider value={context}>{children}</InitialDataContext.Provider>;
}
